// IE 2026 09 25 / ODOO-… (правило Игоря: подсказки только по Alt или опции): НАТИВНЫЕ title ЭЛЕМЕНТОВ ПЛАТФОРМЫ — тем же механизмом, что U1 ядра объяснений:
// «без опции „Показать подсказки“ не показываем подсказки даже при долгом наведении на элемент; только если зажать Alt». Браузерная подсказка по атрибуту
// title всплывает сама через ~1 с наведения и никакому гейту не подчиняется — поэтому атрибут снимается: title → aria-label (подпись для чтения с экрана
// остаётся) + __nativeTitle на элементе + пометка data-native-title (пробы, восстановление). Показ:
// - зажатый Alt — контроллер altTooltip (_find: ветка __nativeTitle) коротким именем в общем элементе подсказки платформы (как короткое имя uiHelp);
// - режим Show tooltips — по наведению после паузы fe.ux.tooltipShowDelayMs (400 мс) тем же элементом (слушатель mouseover, показ мимо гейта обёртки —
//   altTooltip.showing), уход курсора с элемента прячет;
// - обычное наведение — ничего (правило).
// Кого не трогаем: внутренности wijmo (гриды, графики, карта — там title ставит код: обрезанные замороженные ячейки, у них своё «продолжение»),
// подсказки (.wj-tooltip — кнопки-действия), [data-keep-title] (явная просьба владельца), iframe / svg / CodeMirror, элементы с объяснением v-uiHelp
// (директива переносит title сама — __uiHelpTitle), мобильный профиль (body.mobile-ui: CSS-подсказки кнопок по [title]::after — mobile-ui.css).
// Выключатели: fe.ux.nativeTitles = true — нативные title остаются, как было; fe.ux.hoverTooltips = true (прежнее поведение подсказок) — тоже остаются.
// Механика: MutationObserver на body (childList + attributes title, subtree) — обход пачкой в setTimeout(0): добавленные узлы сканируются на [title],
// смена атрибута — только сам элемент (Vue ставит :title заново при смене значения — переносится снова; снял Vue — снимается и наша пометка);
// стартовый обход после установки. Счётчики / консоль — window.nativeTitles (stats, refresh(), restoreAll(), enabled()).
import altTooltip, { nativeTitleHtml } from './altTooltip'

// внутренности wijmo (гриды / графики / карта / прочие контролы), подсказки, помеченные владельцем, чужие документы
const SKIP = '.wj-control, .wj-tooltip, [data-keep-title], iframe, svg, .CodeMirror'
const MARK = 'data-native-title'

const stats = { converted: 0, restored: 0, dropped: 0, scans: 0, batches: 0, mutations: 0, hoverShows: 0, hoverHides: 0 }

const nativeTitles = {
    store: null,
    stats,
    _installed: false,
    _mo: null,
    _added: new Set(),
    _attr: new Set(),
    _flushT: null,
    _hoverEl: null,
    _hoverT: null,

    // переносить ли title: правило действует (нет fe.ux.hoverTooltips), нативные не оставлены явно (fe.ux.nativeTitles), не мобильный профиль
    enabled() {
        try {
            const s = this.store
            if (!s || typeof s.setting !== 'function') return false
            if (s.setting('fe.ux.hoverTooltips', false) === true) return false
            if (s.setting('fe.ux.nativeTitles', false) === true) return false
            if (typeof document !== 'undefined' && document.body && document.body.classList.contains('mobile-ui')) return false
            return true
        } catch (e) { return false }
    },
    skip(el) {
        try { return !el || el.nodeType !== 1 || !!el.__uiHelp || !!(el.closest && el.closest(SKIP)) } catch (e) { return true }
    },
    // title → aria-label + __nativeTitle; true — перенесён (или обновлён)
    convert(el) {
        if (this.skip(el) || !el.hasAttribute('title')) return false
        const tt = el.getAttribute('title')
        if (!tt || !tt.trim()) return false   // пустой title (гасит нативную подсказку предка — напр. .segment-title-text) — не трогаем
        el.__nativeTitle = tt
        el.__nativeTitleRemoving = true
        el.removeAttribute('title')
        if (!el.hasAttribute('aria-label') || el.__nativeAria) { el.setAttribute('aria-label', tt); el.__nativeAria = true }
        el.setAttribute(MARK, '')
        stats.converted++
        return true
    },
    // вернуть нативный title (выключатель включён во время работы, восстановление по запросу)
    restore(el) {
        if (!el || typeof el.__nativeTitle !== 'string') return false
        if (!el.hasAttribute('title')) el.setAttribute('title', el.__nativeTitle)
        this._drop(el)
        stats.restored++
        return true
    },
    // Vue снял title сам — снять и нашу пометку (подсказки по Alt больше нет)
    _drop(el) {
        delete el.__nativeTitle
        delete el.__nativeTitleRemoving
        if (el.__nativeAria) { try { el.removeAttribute('aria-label') } catch (e) { /* элемент ушёл */ } delete el.__nativeAria }
        try { el.removeAttribute(MARK) } catch (e) { /* элемент ушёл */ }
        stats.dropped++
    },
    scan(root) {
        if (!root || !this.enabled()) return 0
        let n = 0
        stats.scans++
        try {
            if (root.nodeType === 1 && root.hasAttribute('title') && this.convert(root)) n++
            if (root.querySelectorAll) for (const el of root.querySelectorAll('[title]')) if (this.convert(el)) n++
        } catch (e) { /* DOM недоступен */ }
        return n
    },
    refresh() { return this.scan(typeof document !== 'undefined' ? document.body : null) },
    restoreAll() {
        let n = 0
        try { for (const el of document.querySelectorAll('[' + MARK + ']')) if (this.restore(el)) n++ } catch (e) { /* нет DOM */ }
        return n
    },

    _onMutations(records) {
        stats.mutations += records.length
        const seen = new Map()   // цель записи → «внутри wijmo»: у перерисовки грида тысячи записей с одной целью (панель ячеек) — closest считается раз
        for (const r of records) {
            if (r.type === 'attributes') { this._attr.add(r.target); continue }
            const t = r.target
            if (t && t.nodeType === 1 && t.closest) {
                let s = seen.get(t)
                if (s === undefined) { s = !!t.closest(SKIP); seen.set(t, s) }
                if (s) continue   // пачки ячеек гридов / svg графиков — мимо
            }
            for (const n of r.addedNodes) if (n.nodeType === 1) this._added.add(n)
        }
        if (!this._flushT && (this._attr.size || this._added.size)) this._flushT = setTimeout(() => this._flush(), 0)
    },
    _flush() {
        this._flushT = null
        stats.batches++
        const attr = [...this._attr], added = [...this._added]
        this._attr.clear(); this._added.clear()
        const on = this.enabled()
        for (const el of attr) {
            if (!el.isConnected) continue
            if (el.hasAttribute('title')) { if (on) this.convert(el); continue }
            // title снят: нами (пометка снимается) либо Vue (наша подсказка по Alt снимается тоже)
            if (el.__nativeTitleRemoving) { delete el.__nativeTitleRemoving; continue }
            if (typeof el.__nativeTitle === 'string') this._drop(el)
        }
        if (!on) return
        for (const n of added) { if (n.isConnected) this.scan(n) }
        if (this._hoverEl && !this._hoverEl.isConnected) this._hoverHide()
    },

    // режим Show tooltips: короткое имя по наведению (как прежний нативный title, тем же элементом подсказки платформы)
    _onOver(e) {
        const s = this.store
        const t = e && e.target
        if (this._hoverEl && (!t || !this._hoverEl.contains(t))) this._hoverHide()
        if (!s || !s.showTooltip || altTooltip.held || !t || !t.closest) return
        const el = t.closest('[' + MARK + ']')
        if (!el || el === this._hoverEl || el.__wjTipFn || el.__wjTipInst) return   // у элемента своя подсказка — её показывает wijmo
        this._hoverEl = el
        let delay = 400
        try { const v = +s.setting('fe.ux.tooltipShowDelayMs', 400); if (v >= 0) delay = v } catch (err) { delay = 400 }
        this._hoverT = setTimeout(() => { this._hoverT = null; this._hoverShow(el) }, delay)
    },
    _hoverShow(el) {
        try {
            if (this._hoverEl !== el || !el.isConnected || altTooltip.held || !this.store?.showTooltip || typeof el.__nativeTitle !== 'string') return
            if (typeof el.matches === 'function' && !el.matches(':hover')) return
            altTooltip.showing = true
            try { altTooltip.tip.show(el, nativeTitleHtml(el.__nativeTitle)) } finally { altTooltip.showing = false }
            this._hoverShown = el
            stats.hoverShows++
        } catch (e) { /* элемент исчез */ }
    },
    _hoverHide() {
        if (this._hoverT) { clearTimeout(this._hoverT); this._hoverT = null }
        const shown = this._hoverShown
        this._hoverEl = null; this._hoverShown = null
        if (!shown || altTooltip._el === shown) return   // по Alt показом владеет контроллер
        try { if (altTooltip.tip.isVisible) { altTooltip.tip.hide(); stats.hoverHides++ } } catch (e) { /* уже скрыт */ }
        try { if (!altTooltip._el && !altTooltip._lingerEl) altTooltip.lastKey = null } catch (e) { /* нет контроллера */ }   // дедуп обёртки show в App.vue: та же подсказка показывается снова
    },

    install(store) {
        this.store = store
        if (this._installed || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return
        this._installed = true
        this._onOverBnd = (e) => this._onOver(e)
        document.addEventListener('mouseover', this._onOverBnd, true)
        this._mo = new MutationObserver((records) => this._onMutations(records))
        this._mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['title'] })
        setTimeout(() => this.refresh(), 0)
        window.nativeTitles = this
    },
    uninstall() {
        if (!this._installed) return
        this._installed = false
        if (this._mo) { this._mo.disconnect(); this._mo = null }
        document.removeEventListener('mouseover', this._onOverBnd, true)
        if (this._flushT) { clearTimeout(this._flushT); this._flushT = null }
        this._attr.clear(); this._added.clear()
        this._hoverHide()
    },
}

export default nativeTitles
